import { LoaderCircle, Plus } from 'lucide-react';
import { ElementRef, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { AudienceCard } from './components/AudienceCard';

import { AnimateSpin } from '@/components/AnimateSpin';
import { Card, CardContent } from '@/components/ui/Card';
import { ROUTE_PATHS } from '@/config/constants';
import { useAuth } from '@/contexts/AuthContext';
import { useGetAudiences } from '@/hooks/useGetAudiences';
import useGetAudiencesDefault from '@/hooks/useGetAudiencesDefault';

export function Audiences() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Behaviors',
  });
  const endAudiencesRef = useRef<ElementRef<'div'>>(null);

  const navigate = useNavigate();

  const { user } = useAuth();

  const {
    audiences,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetAudiences();

  const { audiencesDefault, isLoading } = useGetAudiencesDefault();

  function handleCreateAudienceNavigate() {
    if (user?.customer?.freemium) {
      toast.error(t('toast.notAvailable'));
      return;
    }

    navigate(ROUTE_PATHS.AUDIENCES_CREATE);
  }

  useEffect(() => {
    if (!endAudiencesRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        const { isIntersecting } = entries[0];

        if (!hasNextPage) {
          obs.disconnect();
          return;
        }

        if (isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        rootMargin: '30px',
      },
    );

    observer.observe(endAudiencesRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  return (
    <div className="container py-8">
      <section className="mb-10 flex items-center justify-between">
        <h1 className="text-[42px] tracking-[8.25px]">{t('title')}</h1>
      </section>

      <section className="mb-10">
        <h2 className="mb-4 text-xl font-semibold">
          {t('suggestedBehaviors.title')}
        </h2>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {isLoading && <AnimateSpin />}

          {!isLoading &&
            audiencesDefault.map((audience) => (
              <AudienceCard key={audience.id} audience={audience} />
            ))}
        </div>
      </section>

      <section>
        <h2 className="mb-4 text-xl font-semibold">
          {t('customBehavior.title')}
        </h2>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {audiences.map((audience) => (
            <AudienceCard key={audience.id} audience={audience} />
          ))}

          <Card
            className="flex cursor-pointer flex-col items-center justify-center border-2 border-dashed transition-colors hover:border-gray-400 hover:bg-gray-200"
            onClick={handleCreateAudienceNavigate}
          >
            <CardContent className="flex h-[180px] flex-col items-center justify-center gap-2">
              <Plus size={24} />
              <span className="text-center text-sm font-medium">
                {t('customBehavior.createNewBehaviorLabel')}
              </span>
            </CardContent>
          </Card>
        </div>
      </section>

      <div ref={endAudiencesRef}>
        {(isFetching || isFetchingNextPage) && (
          <LoaderCircle className="mx-auto mt-10 animate-spin" />
        )}
      </div>
    </div>
  );
}
